'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FamilyMember {
  id: string;
  name: string;
  avatar?: string;
  isOnline: boolean;
  lastActive?: string;
}

interface FamilyActivityIndicatorProps {
  members?: FamilyMember[];
  showDetails?: boolean;
  className?: string;
}

const FamilyActivityIndicator = ({
  members = [],
  showDetails = false,
  className = '',
}: FamilyActivityIndicatorProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const onlineMembers = members.filter((member) => member.isOnline);
  const onlineCount = onlineMembers.length;

  if (onlineCount === 0) return null;

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-smooth"
        aria-label={`${onlineCount} family member${
          onlineCount > 1 ? 's' : ''
        } online`}
      >
        <div className="relative">
          <Icon
            name="UsersIcon"
            size={20}
            className="text-success"
          />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-success border-2 border-card"></span>
        </div>
        {showDetails && (
          <span className="text-sm font-caption text-muted-foreground">
            {onlineCount} online
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-popover border border-border rounded-lg shadow-warm-md z-50 animate-scale-in">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-caption font-medium text-foreground">
                Family Activity
              </h3>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 rounded hover:bg-muted transition-smooth"
                aria-label="Close"
              >
                <Icon
                  name="XMarkIcon"
                  size={16}
                  className="text-muted-foreground"
                />
              </button>
            </div>
            <ul className="space-y-2">
              {onlineMembers.map((member) => (
                <li
                  key={member.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-smooth"
                >
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-caption font-medium text-primary">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-success border-2 border-popover"></span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-caption font-medium text-foreground truncate">
                      {member.name}
                    </p>
                    <p className="text-xs font-caption text-success">
                      Active now
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyActivityIndicator;